import React, { useState } from 'react';
import { LogIn, User, Lock } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { authAPI } from '../services/api';
import { toast } from 'sonner';

const Login = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  // 🛡️ FIX 27-06-2026: retry para erros transientes (network/502/503/504/520-524).
  // O interceptor global em services/api.js já tenta 5x silenciosamente — esta
  // wrapper local apenas captura o erro final e dá um aviso "user-friendly"
  // (incluindo o HTTP status e nº de tentativas para diagnóstico).
  const tentarLogin = async (creds) => {
    try {
      return await authAPI.login(creds);
    } catch (error) {
      // O interceptor já fez retries — calcular nº efectivo p/ mensagem
      error._tentativas = (error.config?.__retryCount || 0) + 1;
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      console.log('🔐 Tentando login com:', username);
      const response = await tentarLogin({ username, password });
      console.log('✅ Login bem-sucedido:', response.data);
      toast.success('Login efetuado com sucesso!');
      onLogin(response.data.user, response.data.access_token);
    } catch (error) {
      console.error('❌ Erro no login:', error);
      console.error('Resposta do servidor:', error.response);
      
      let mensagemErro = 'Erro ao fazer login';
      const status = error.response?.status;
      const tipo = error.response?.data?.type;
      const tentativas = error._tentativas || 1;
      const sufixoCodigo = status ? ` [HTTP ${status}]` : ' [sem resposta]';
      const sufixoRetry = tentativas > 1 ? ` (após ${tentativas} tentativas)` : '';

      if (error.code === 'ERR_NETWORK' || !error.response) {
        mensagemErro = `⚠️ Sem ligação ao servidor${sufixoRetry}. Verifique a sua rede ou tente daqui a 1 minuto.`;
      } else if (status === 404) {
        mensagemErro = `⚠️ Endpoint não encontrado${sufixoCodigo}. Contacte o suporte.`;
      } else if (tipo === 'DatabaseUnavailable' || status === 503) {
        mensagemErro = `⚠️ Base de dados temporariamente indisponível${sufixoCodigo}${sufixoRetry}.`;
      } else if (status === 520 || status === 502 || status === 504) {
        mensagemErro = `⚠️ Servidor temporariamente indisponível${sufixoCodigo}${sufixoRetry}. Aguarde 1-2 min.`;
      } else if (status === 401) {
        mensagemErro = 'Utilizador ou palavra-passe incorretos';
      } else if (status === 500) {
        mensagemErro = `⚠️ Erro interno do servidor${sufixoCodigo}. Contacte o suporte.`;
      } else {
        mensagemErro = (error.response?.data?.detail || error.message || mensagemErro) + sufixoCodigo;
      }
      
      toast.error(mensagemErro);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-hero flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-elegant">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="h-16 w-16 rounded-full bg-gradient-primary flex items-center justify-center shadow-glow">
              <LogIn className="h-8 w-8 text-white" />
            </div>
          </div>
          <CardTitle className="text-3xl font-display">JANEIRA</CardTitle>
          <CardDescription>Entre com suas credenciais</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">Utilizador</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="username"
                  type="text"
                  placeholder="Digite seu utilizador"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Palavra-passe</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  placeholder="Digite sua palavra-passe"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            </div>

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'Entrando...' : 'Entrar'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
